import { AsyncPipe } from "@angular/common";
import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { BehaviorSubject } from "rxjs";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { EntityType } from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { Atgard, SkogsvardEntity } from "../../models/atgard-models";
import { Kontrakt } from "../../models/kontrakt-models";
import { CreateSkogsvard, SkogsvardSelectAtgardItem } from "../../models/skogsvard/createskogsvard-models";
import { AtgardKedjaMediatorService } from "../../services/atgardkedja-mediator.service";
import { SkogsvardService } from "../../services/skogsvard.service";
import { VKAtgardHelper } from "../../utils/vkatgard-helper";
import { SkogsvardCreateRowComponent } from "./skogsvard-create-row/skogsvard-create-row.component";
import { SkogsvardSelectAtgardComponent } from "./skogsvard-select-atgard/skogsvard-select-atgard.component";

@Component({
    selector: "rask-skogsvard-create-dialog",
    styleUrls: ["./skogsvard-create-dialog.component.scss"],
    templateUrl: "./skogsvard-create-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SimpleSelectorComponent,
        SkogsvardSelectAtgardComponent,
        SkogsvardCreateRowComponent,
        ActionButtonComponent,
        MessagePanelComponent,
        ButtonComponent,
        AsyncPipe
    ]
})
export class SkogsvardCreateDialogComponent implements OnInit {
    atgarder: Atgard[];
    atgarderPreload: EntityDTO[];
    kontraktList: Kontrakt[];
    modal = inject(NgbActiveModal);
    @Input() preSelectedAtgard?: string;
    @Input() preSelectedKontraktGuid: string;
    @Input() readonly = false;

    protected listOfAtgarder: SkogsvardSelectAtgardItem[] = [];
    protected selectKontraktForSkogsvardItems: SelectionItem[];
    protected skogsvard: CreateSkogsvard;
    protected skogsvardGrupper: string[] = [];

    private readonly atgardKedjaMediatorService = inject(AtgardKedjaMediatorService);
    private readonly infoMessages: IMessageModel[] = [];
    private readonly messagesSrc = new BehaviorSubject<IMessageModel[]>([]);
    private readonly skogsvardService = inject(SkogsvardService);

    private anySiktrojningAndInvalidProduktionsstatus = false;
    private arbetsorderProduktionsstatus: string;
    protected get messages$() {
        return this.messagesSrc.asObservable();
    }

    ngOnInit(): void {
        this.skogsvard = new CreateSkogsvard();

        this.skogsvardService.getSkogsvardGrupper().subscribe(grupper => {
            this.skogsvardGrupper = grupper;
        });

        this.setAtgarderSelectList();
        this.selectKontraktForSkogsvardItems = this.getKontraktSelectionItems();

        this.skogsvardAtgardChanged(this.listOfAtgarder.find(atg => atg.value === this.preSelectedAtgard));
    }

    protected addSkogsvardRow(): void {
        this.skogsvard.addRow();
        this.validate();
    }

    protected canSave(): boolean {
        const anyMetodOrGroupMissing = this.skogsvard.skogsvardRowTypes
            .some(row => (!(row.skogsvardGrupp && row.skogsvardTyp)));

        return !anyMetodOrGroupMissing &&
            !this.anySiktrojningAndInvalidProduktionsstatus;
    }

    protected close(): void {
        this.modal.dismiss();
    }

    protected deleted(index: number): void {
        this.skogsvard.skogsvardRowTypes.splice(index, 1);
        this.validate();
    }

    protected getKontraktSelectionItems(): SelectionItem[] {
        return this.kontraktList.map((k) => ({
            value: k.guid,
            text: `Skogsvård på rundvirkeskontraktet (${ k.kontraktnummer })`,
            selected: true
        }));
    }

    protected rowChanged(): void {
        this.validate();
    }

    protected save(): void {
        this.modal.close(this.skogsvard);
    }

    protected skogsvardAtgardChanged(item: SkogsvardSelectAtgardItem): void {
        this.skogsvard.atgardCopyGuid = item?.value;
        this.validate();
    }

    private addMessage(message: IMessageModel): void {
        this.infoMessages.push(message);
    }

    private clearMessages(): void {
        this.infoMessages.splice(0);
        this.messagesSrc.next([]);
    }

    private getAtgarderforSelect(list: SkogsvardSelectAtgardItem[]) {
        let index = 0;
        this.atgarder.forEach((atg) => {
            const entity = this.atgarderPreload.find(x => x.guid === atg.guid);
            if(entity?.type !== EntityType.virkeskopsAtgardSkogsvard && atg.geometri && atg.nettoareal) {
                const selectionItem = new SkogsvardSelectAtgardItem(
                    null,
                    null,
                    atg.guid,
                    atg.avverkningsTyp?.grupp,
                    `${atg.beteckning} - ${atg.nettoareal} ha`,
                    index,
                    false
                );
                list.push(selectionItem);
                index++;
            }
        });
    }

    private getSkogsvardAtgarderforSelect(list: SkogsvardSelectAtgardItem[]) {
        const skogsvardLista = this.atgarderPreload.filter(x => VKAtgardHelper.isAtgardEntitySkogsvard(x)) as SkogsvardEntity[];
        const skogsvardListSorted = this.atgardKedjaMediatorService.getSorteradAtgardslista(skogsvardLista);

        let index = 0;
        skogsvardListSorted.forEach((entity) => {
            const atg = this.atgarder.find(x => x.guid === entity.guid);

            if(atg.geometri && atg.nettoareal) {
                const selectionItem = new SkogsvardSelectAtgardItem(
                    null,
                    null,
                    atg.guid,
                    atg.atgardstyp,
                    `${atg.beteckning} - ${atg.nettoareal} ha`,
                    index,
                    true
                );
                list.push(selectionItem);
            }
            index++;
        });
    }

    private isAnySiktrojningAndInvalidProduktionsstatus(): boolean {
        if(this.skogsvard?.atgardCopyGuid) {
            const atgardsKedja = this.atgardKedjaMediatorService.getAtgardKedjaKopplingarByAtgard(this.skogsvard?.atgardCopyGuid);
            this.arbetsorderProduktionsstatus = this.skogsvardService.getArbetsorderProduktionsstatus(this.atgarder, atgardsKedja, this.skogsvard.atgardCopyGuid);

            for (const row of this.skogsvard.skogsvardRowTypes) {
                if (this.skogsvardService.isSiktrojning(row.skogsvardTyp, row.skogsvardGrupp) &&
                    !this.skogsvardService.isValidProduktionsstatusForSiktrojning(this.arbetsorderProduktionsstatus)) {
                    return true;
                }
            }
        }
        return false;
    }

    private setAtgarderSelectList(): void {
        const listOfAtgarder: SkogsvardSelectAtgardItem[] = [];
        this.getAtgarderforSelect(listOfAtgarder);
        this.getSkogsvardAtgarderforSelect(listOfAtgarder);
        this.listOfAtgarder = listOfAtgarder;
    }

    private showMessages(): void {
        if (this.infoMessages.length > 0) {
            this.messagesSrc.next(this.infoMessages);
        }
    }

    private validate(): void {
        this.anySiktrojningAndInvalidProduktionsstatus = this.isAnySiktrojningAndInvalidProduktionsstatus();

        this.clearMessages();
        if (this.anySiktrojningAndInvalidProduktionsstatus) {
            const message = {
                severity: MessageSeverity.Error,
                message: `När arbetsordern för den avverkningsåtgärd som skogsvården är kopplad till har produktionsstatus "${  this.arbetsorderProduktionsstatus  }",
                går det inte att skapa Siktröjning eller NS-siktröjning för den åtgärden.`
            } as IMessageModel;

            this.addMessage(message);
        }

        this.showMessages();
    }
}
